import React from 'react';
import { Box, Grid, useTheme } from '@mui/material';
import { useResponsiveUtils } from '@utils/responsiveUtils';
import { useAppTheme } from '@contexts/ThemeContext';

interface ResponsiveGridProps {
    children: React.ReactNode;
    maxColumns?: number;
    minColumnWidth?: number;
    spacing?: number;
    autoFit?: boolean;
    equalHeight?: boolean;
    centerItems?: boolean;
    customBreakpoints?: {
        xxs?: number;
        xs?: number;
        sm?: number;
        md?: number;
        lg?: number;
        xl?: number;
        xxl?: number;
    };
    className?: string;
    sx?: any;
}

export const ResponsiveGrid: React.FC<ResponsiveGridProps> = ({
    children,
    maxColumns = 4,
    minColumnWidth = 250,
    spacing = 2,
    autoFit = true,
    equalHeight = false,
    centerItems = false,
    customBreakpoints,
    className,
    sx = {}
}) => {
    const theme = useTheme();
    const { isDarkMode } = useAppTheme();
    const {
        isXxs,
        isSmallMobile,
        isMediumMobile,
        isSmallTablet,
        isLargeTablet,
        isSmallDesktop,
        isLargeDesktop,
        getGridResponsive,
        getSpacingResponsive
    } = useResponsiveUtils();

    // Get responsive grid configuration
    const gridConfig = getGridResponsive(maxColumns);

    // Determine columns based on screen size and custom breakpoints
    const getColumns = () => {
        if (customBreakpoints) {
            if (isXxs) return customBreakpoints.xxs || 1;
            if (isSmallMobile) return customBreakpoints.xs || 1;
            if (isMediumMobile) return customBreakpoints.sm || 2;
            if (isSmallTablet) return customBreakpoints.md || 2;
            if (isLargeTablet) return customBreakpoints.lg || 3;
            if (isSmallDesktop) return customBreakpoints.xl || 4;
            if (isLargeDesktop) return customBreakpoints.xxl || 4;
        }

        return gridConfig.columns;
    };

    const columns = getColumns();

    // Convert children to array for processing
    const childArray = React.Children.toArray(children);

    if (autoFit) {
        // Use CSS Grid for auto-fit layout with enhanced responsiveness
        const responsiveMinWidth = isXxs ? Math.max(minColumnWidth * 0.6, 200) :
                                  isSmallMobile ? Math.max(minColumnWidth * 0.7, 220) :
                                  isMediumMobile ? Math.max(minColumnWidth * 0.8, 240) :
                                  minColumnWidth;

        return (
            <Box
                className={className}
                sx={{
                    display: 'grid',
                    gridTemplateColumns: `repeat(auto-fit, minmax(${responsiveMinWidth}px, 1fr))`,
                    gap: gridConfig.gap,
                    width: '100%',
                    alignItems: equalHeight ? 'stretch' : 'start',
                    justifyItems: centerItems ? 'center' : 'stretch',
                    padding: getSpacingResponsive(4),
                    ...sx
                }}
            >
                {childArray.map((child, index) => (
                    <Box
                        key={index}
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            height: equalHeight ? '100%' : 'auto',
                            minWidth: 0, // Prevent overflow
                            width: '100%',
                        }}
                    >
                        {child}
                    </Box>
                ))}
            </Box>
        );
    }

    // Use fixed columns layout with enhanced responsiveness
    return (
        <Box
            className={className}
            sx={{
                display: 'grid',
                gridTemplateColumns: `repeat(${columns}, 1fr)`,
                gap: gridConfig.gap,
                width: '100%',
                alignItems: equalHeight ? 'stretch' : 'start',
                justifyItems: centerItems ? 'center' : 'stretch',
                padding: getSpacingResponsive(4),
                ...sx
            }}
        >
            {childArray.map((child, index) => (
                <Box
                    key={index}
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        height: equalHeight ? '100%' : 'auto',
                        minWidth: 0, // Prevent overflow
                        width: '100%',
                    }}
                >
                    {child}
                </Box>
            ))}
        </Box>
    );
};

export default ResponsiveGrid;